import { Checkbox, Col, Collapse, Input, InputNumber, Radio, Row, Slider, Space } from 'antd';
import { TemplateConfig } from '../../../Templar/RenderPdfPrescription';
import { AntTooltip } from '../../../components/common/AntTooltip';
import {
    getMedications1Html,
    medicationFormatToTableMapping,
    medOptions1,
    medOptions2,
    medOptions3,
    medOptions4,
} from '../../../Templar/templateUtils';
const { Panel } = Collapse;

const medicationFormatToTableColumnSizeMapping = {
    'quantity-column': medOptions2,
    'quantity-column-without-border': medOptions3,
    'quantity-remarks-column-without-border': medOptions4,
};

const Medications: React.FC<{
    rxConfig: TemplateConfig | undefined;
    saveRxConfigInState: (rxObj: TemplateConfig) => void;
    sectionName?: string;
}> = ({ rxConfig, saveRxConfigInState, sectionName }) => {
    const medTableColumnSizes =
        medicationFormatToTableColumnSizeMapping[
            rxConfig?.medication_table_format as keyof typeof medicationFormatToTableMapping
        ] || medOptions1;

    return (
        <Collapse
            expandIcon={() => (
                <div className="rounded-4 w-26 h-26 flex items-center justify-center bg-darwin-accent-medicines-pink-800">
                    <div className="w-full h-full flex items-center justify-center">
                        <svg
                            className="w-16 h-12 fill-white"
                            role="img"
                            viewBox="0 0 16 13"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M3.76543 5C1.86699 5 0.296678 6.35938 0.0154276 8.14062C0.0154276 8.28125 0.109178 8.375 0.226365 8.375H7.32793C7.44512 8.375 7.53887 8.28125 7.51543 8.16406C7.23418 6.38281 5.6873 5 3.76543 5ZM7.32793 9.125H0.226365C0.109178 9.125 0.0154276 9.24219 0.0388651 9.35938C0.320115 11.1406 1.86699 12.5 3.76543 12.5C5.64043 12.5 7.21074 11.1406 7.49199 9.35938C7.53887 9.24219 7.44512 9.125 7.32793 9.125ZM8.52324 2.04688C8.45293 1.97656 8.3123 1.97656 8.24199 2.07031C7.1873 3.54688 7.30449 5.58594 8.61699 6.92188C9.95293 8.23438 11.992 8.35156 13.4451 7.29688C13.5389 7.22656 13.5389 7.08594 13.4686 7.01562L8.52324 2.04688ZM13.9373 1.60156C12.6014 0.289062 10.5623 0.171875 9.10918 1.22656C9.01543 1.29688 9.01543 1.4375 9.08574 1.50781L14.0311 6.47656C14.1248 6.54688 14.2654 6.54688 14.3357 6.45312C15.367 4.97656 15.2498 2.9375 13.9373 1.60156Z"></path>
                        </svg>
                    </div>
                </div>
            )}
        >
            <Panel header={<div className="font-700">{sectionName || 'Medications'}</div>} key="1">
                <p className="mr-16 space-y-4">
                    <div>
                        <span className="font-700">Medication Table</span>

                        <Radio.Group
                            onChange={(e) => {
                                saveRxConfigInState({
                                    medication_table_format: e.target.value,
                                    medication_table_width: undefined,
                                });
                            }}
                            value={rxConfig?.medication_table_format ?? ''}
                            className="flex flex-col"
                        >
                            <Radio value={''}>Default Table{'    '}</Radio>
                            <Radio value={'quantity-column'}>
                                Boxed Table with quantity column
                            </Radio>
                            <Radio value={'quantity-column-without-border'}>
                                Open Table with quantity column
                            </Radio>{' '}
                            <Radio value={'quantity-remarks-column-without-border'}>
                                Open Table with quantity & remarks column
                            </Radio>
                        </Radio.Group>
                    </div>
                    <Collapse
                        ghost
                        expandIcon={({ isActive }) => (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className="w-10 h-10 fill-darwin-neutral-700"
                                style={{
                                    rotate: isActive ? '180deg' : '90deg',
                                    transitionDuration: '0.2s',
                                }}
                            >
                                <path d="M290.5 51.8C283.3 39.5 270.2 32 256 32s-27.3 7.5-34.5 19.8l-216 368c-7.3 12.4-7.3 27.7-.2 40.1S25.7 480 40 480H472c14.3 0 27.6-7.7 34.7-20.1s7-27.8-.2-40.1l-216-368z" />
                            </svg>
                        )}
                    >
                        <Panel
                            header="Individual Column Width"
                            key={1}
                            className="pmh-collapse"
                            extra={
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        saveRxConfigInState({
                                            medication_table_width: undefined,
                                        });
                                    }}
                                    className="flex items-center space-x-4 cursor-pointer"
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                        className="w-18 h-18 fill-neutral-1 cursor-pointer"
                                    >
                                        <path d="M48.5 224H40c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2L98.6 96.6c87.6-86.5 228.7-86.2 315.8 1c87.5 87.5 87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3c-62.2-62.2-162.7-62.5-225.3-1L185 183c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H48.5z"></path>
                                    </svg>
                                </div>
                            }
                        >
                            <div className="space-y-4">
                                {Object.entries(medTableColumnSizes.medIdToNameMappingForWidth).map(
                                    (data) => {
                                        const id = data[0];
                                        const name = data[1];

                                        return (
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center justify-between">
                                                    <div>{name}</div>{' '}
                                                </div>

                                                <Col span={12}>
                                                    <Slider
                                                        value={
                                                            rxConfig?.medication_table_width?.[
                                                                id
                                                            ] ||
                                                            medTableColumnSizes
                                                                ?.allColumnWidthInNumber?.[
                                                                id as keyof typeof medTableColumnSizes.allColumnWidthInNumber
                                                            ]
                                                        }
                                                        min={0}
                                                        max={100}
                                                        step={1}
                                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                                        // @ts-ignore
                                                        railStyle={{
                                                            background: '#cccfdb',
                                                        }}
                                                        onChange={(value) => {
                                                            saveRxConfigInState({
                                                                medication_table_width: {
                                                                    ...(rxConfig?.medication_table_width ||
                                                                        {}),
                                                                    [id]: value || undefined,
                                                                },
                                                            });
                                                        }}
                                                    />
                                                </Col>
                                            </div>
                                        );
                                    },
                                )}
                            </div>
                        </Panel>
                    </Collapse>
                    <div>
                        <span className="font-700">Medication style</span>
                        <div className="cursor-pointer">
                            <p
                                className="cursor-pointer flex flex-1 items-center justify-between"
                                onClick={() => {
                                    saveRxConfigInState({
                                        medication_name_in_capital: !rxConfig?.medication_name_in_capital,
                                    });
                                }}
                            >
                                <span>Capitalize name</span>
                                <Checkbox checked={rxConfig?.medication_name_in_capital}></Checkbox>
                            </p>
                        </div>
                        <div className="cursor-pointer">
                            <p
                                className="cursor-pointer flex flex-1 items-center justify-between"
                                onClick={() => {
                                    saveRxConfigInState({
                                        make_generic_name_as_primary: !rxConfig?.make_generic_name_as_primary,
                                    });
                                }}
                            >
                                <span>Generic as Primary Name</span>
                                <Checkbox
                                    checked={rxConfig?.make_generic_name_as_primary}
                                ></Checkbox>
                            </p>
                        </div>

                        <div className="cursor-pointer">
                            <p
                                className="cursor-pointer flex flex-1 items-center justify-between"
                                onClick={() => {
                                    saveRxConfigInState({
                                        medication_heading_hide: !rxConfig?.medication_heading_hide,
                                    });
                                }}
                            >
                                <span>Hide Table Heading</span>
                                <Checkbox checked={rxConfig?.medication_heading_hide}></Checkbox>
                            </p>
                        </div>

                        {rxConfig?.medication_heading_hide ? null : (
                            <div className="cursor-pointer pt-8">
                                <p className="cursor-pointer flex flex-1 items-center justify-between">
                                    <span className="whitespace-nowrap">Heading Name</span>
                                    <Input
                                        style={{
                                            margin: '0 0 0 16px',
                                        }}
                                        size="small"
                                        defaultValue={
                                            rxConfig?.medication_table_heading_text ||
                                            'PRESCRIPTION'
                                        }
                                        value={rxConfig?.medication_table_heading_text}
                                        onChange={(e) => {
                                            saveRxConfigInState({
                                                medication_table_heading_text: e.target.value,
                                            });
                                        }}
                                    />
                                </p>
                            </div>
                        )}
                    </div>

                    {rxConfig?.medication_heading_hide ? null : (
                        <div>
                            <div className="space-y-4">
                                <span className="font-700">Heading alignment</span>

                                <div className="flex justify-center">
                                    <Radio.Group
                                        value={rxConfig?.medications_table_title_alignment}
                                        size="middle"
                                        onChange={(e) => {
                                            saveRxConfigInState({
                                                medications_table_title_alignment: e.target.value,
                                            });
                                        }}
                                    >
                                        <Radio.Button
                                            style={{
                                                paddingLeft: '16px',
                                                paddingRight: '16px',
                                            }}
                                            value="left"
                                        >
                                            Left
                                        </Radio.Button>
                                        <Radio.Button
                                            style={{
                                                paddingLeft: '16px',
                                                paddingRight: '16px',
                                            }}
                                            value="center"
                                        >
                                            Center
                                        </Radio.Button>
                                        <Radio.Button
                                            style={{
                                                paddingLeft: '16px',
                                                paddingRight: '16px',
                                            }}
                                            value="right"
                                        >
                                            Right
                                        </Radio.Button>
                                    </Radio.Group>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="border-b border-darwin-neutral-200 mx-8"></div>
                    <div className="flex items-center justify-between">
                        <div>Table title background color:</div>{' '}
                        <div className="flex space-x-12">
                            <input
                                type="color"
                                id="head"
                                name="head"
                                value={rxConfig?.medications_table_title_bg_color || '#E4DEEB'}
                                onChange={(e) => {
                                    saveRxConfigInState({
                                        medications_table_title_bg_color: e.target.value,
                                    });
                                }}
                                className="circular-color-input"
                            />

                            <AntTooltip content="Reset">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className="w-18 h-18 fill-neutral-1 cursor-pointer"
                                    onClick={() => {
                                        saveRxConfigInState({
                                            medications_table_title_bg_color: '',
                                        });
                                    }}
                                >
                                    <path d="M48.5 224H40c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2L98.6 96.6c87.6-86.5 228.7-86.2 315.8 1c87.5 87.5 87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3c-62.2-62.2-162.7-62.5-225.3-1L185 183c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H48.5z"></path>
                                </svg>
                            </AntTooltip>
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div>Table border color:</div>{' '}
                        <div className="flex space-x-12">
                            <input
                                type="color"
                                id="head"
                                name="head"
                                value={rxConfig?.medications_table_border_color || '#8064A2'}
                                onChange={(e) => {
                                    saveRxConfigInState({
                                        medications_table_border_color: e.target.value,
                                    });
                                }}
                                className="circular-color-input"
                            />

                            <AntTooltip content="Reset">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className="w-18 h-18 fill-neutral-1 cursor-pointer"
                                    onClick={() => {
                                        saveRxConfigInState({
                                            medications_table_border_color: '',
                                        });
                                    }}
                                >
                                    <path d="M48.5 224H40c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2L98.6 96.6c87.6-86.5 228.7-86.2 315.8 1c87.5 87.5 87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3c-62.2-62.2-162.7-62.5-225.3-1L185 183c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H48.5z"></path>
                                </svg>
                            </AntTooltip>
                        </div>
                    </div>
                    <div className="flex items-center justify-between">
                        <div>Table Heading color:</div>{' '}
                        <div className="flex space-x-12">
                            <input
                                type="color"
                                id="head"
                                name="head"
                                value={rxConfig?.medications_table_header_color || '#8064A2'}
                                onChange={(e) => {
                                    saveRxConfigInState({
                                        medications_table_header_color: e.target.value,
                                    });
                                }}
                                className="circular-color-input"
                            />

                            <AntTooltip content="Reset">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className="w-18 h-18 fill-neutral-1 cursor-pointer"
                                    onClick={() => {
                                        saveRxConfigInState({
                                            medications_table_header_color: '',
                                        });
                                    }}
                                >
                                    <path d="M48.5 224H40c-13.3 0-24-10.7-24-24V72c0-9.7 5.8-18.5 14.8-22.2s19.3-1.7 26.2 5.2L98.6 96.6c87.6-86.5 228.7-86.2 315.8 1c87.5 87.5 87.5 229.3 0 316.8s-229.3 87.5-316.8 0c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0c62.5 62.5 163.8 62.5 226.3 0s62.5-163.8 0-226.3c-62.2-62.2-162.7-62.5-225.3-1L185 183c6.9 6.9 8.9 17.2 5.2 26.2s-12.5 14.8-22.2 14.8H48.5z"></path>
                                </svg>
                            </AntTooltip>
                        </div>
                    </div>
                </p>
            </Panel>
        </Collapse>
    );
};

export default Medications;
